import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HashLoader } from "react-spinners";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import QueryResearchResult from "./QueryResearchResult";
import QueryResearchSearchBox from "./QueryResearchSearchBox";
import { getLocalStorageWithExpiry, showApiError, clearAuthStorage } from "@/utils";
import * as C from "@/const";
import "@/pages/page.css";
import "./queryResearch.css";

function QueryResearch() {
  const { t } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [doExplicitWebSearch, setDoExplicitWebSearch] = useState(() =>
    getLocalStorageWithExpiry(C.DO_EXPLICIT_WEB_SEARCH_KEY),
  );
  const [enableSearchLocalization, setEnableSearchLocalization] = useState(() =>
    getLocalStorageWithExpiry(C.ENABLE_SEARCH_LOCALIZATION_KEY),
  );
  const navigate = useNavigate();
  const location = useLocation();
  const allPlatforms = React.useMemo(
    () => ["chatgpt", "gemini", "perplexity", "claude"],
    [],
  );
  const [query, setQuery] = useState("");
  const [adhocQuery, setAdhocQuery] = useState(null);
  const [LLM_Platforms, setLLM_Platforms] = useState(allPlatforms);
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [queryResearchData, setQueryResearchData] = useState(null);
  const [isQueryResearchLoaded, setIsQueryResearchLoaded] = useState(true);
  const [queryResearchError, setQueryResearchError] = useState(null);
  const [selectedProviders, setSelectedProviders] = useState(allPlatforms);
  const [isAddingToTracked, setIsAddingToTracked] = useState(false);
  const [isAddedToTracked, setIsAddedToTracked] = useState(false);

  // Handle storage changes
  useEffect(() => {
    const handleStorageChange = (event) => {
      try {
        const newValue = JSON.parse(event.newValue);
        if (event.key === C.DO_EXPLICIT_WEB_SEARCH_KEY) {
          setDoExplicitWebSearch(newValue.value);
        }
        if (event.key === C.ENABLE_SEARCH_LOCALIZATION_KEY) {
          setEnableSearchLocalization(newValue.value);
        }
      } catch (error) {
        console.error("Error parsing storage change:", error);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Checkbox -- Reset selected providers when new data is loaded
  useEffect(() => {
    if (queryResearchData?.data?.returned_llm_providers) {
      setSelectedProviders(queryResearchData.data.returned_llm_providers);
    } else if (queryResearchData === null) {
      // Reset to all platforms when data is cleared (new search started)
      setSelectedProviders(allPlatforms);
    }
  }, [queryResearchData, allPlatforms]);

  // Update adhocQuery when queryResearchData loads
  useEffect(() => {
    if (queryResearchData && query) {
      setAdhocQuery(query);
    }
  }, [queryResearchData, query]);

  // Checkbox -- Filter data based on selected providers
  const filteredQueryResearchData = React.useMemo(() => {
    if (!queryResearchData?.data?.llm_responses) return null;

    const filteredLLMResponses = queryResearchData.data.llm_responses.filter(
      (response) => selectedProviders.includes(response.llm_provider),
    );

    return {
      ...queryResearchData,
      data: {
        ...queryResearchData.data,
        llm_responses: filteredLLMResponses,
        returned_llm_providers: filteredLLMResponses.map(
          (response) => response.llm_provider,
        ),
      },
    };
  }, [queryResearchData, selectedProviders]);

  // Handler for checkbox changes
  const handleProviderToggle = (provider) => {
    setSelectedProviders((prev) => {
      if (prev.includes(provider)) {
        return prev.filter((p) => p !== provider);
      } else {
        return [...prev, provider];
      }
    });
  };

  // Handler for "Select All" / "Deselect All"
  const handleSelectAllProviders = () => {
    const availableProviders =
      queryResearchData?.data?.returned_llm_providers || [];

    if (selectedProviders.length === availableProviders.length) {
      setSelectedProviders([]);
    } else {
      setSelectedProviders(availableProviders);
    }
  };

  // Handle platform change
  const handlePlatformChange = (e) => {
    const selectedValue = e.target.value;

    if (selectedValue === "All GenAI Search Apps") {
      setLLM_Platforms(allPlatforms);
    } else {
      setLLM_Platforms([selectedValue]);
    }
  };

  const handleSearchButton = async () => {
    setQueryResearchData(null);
    setIsQueryResearchLoaded(false);
    setQueryResearchError(null);
    setIsAddedToTracked(false);

    const requestBody = {
      query: query,
      requested_llm_providers: LLM_Platforms,
    };
    if (doExplicitWebSearch !== undefined && doExplicitWebSearch !== null) {
      requestBody.do_explicit_web_search = doExplicitWebSearch;
    }
    if (
      enableSearchLocalization !== undefined &&
      enableSearchLocalization !== null
    ) {
      requestBody.enable_search_localization = enableSearchLocalization;
    }

    const url = `${BACKEND_URL}/api/v1/query-research`;

    try {
      const response = await axios.post(url, requestBody, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      setQueryResearchData(response.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      console.error("Error details:", error.response?.data);
      console.error("Status:", error.response?.status);
      console.error("Request body:", requestBody);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access - redirecting to login");
        clearAuthStorage();
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/login");
        return;
      } else if (error.response?.status === 422) {
        const msg = t(
          "promptResearch.invalidRequestFormat",
          "Invalid request format. Please check your input.",
        );
        setQueryResearchError(msg);
        showApiError(msg);
      } else {
        const msg = t(
          "promptResearch.fetchDataError",
          "An error occurred while fetching data.",
        );
        setQueryResearchError(msg);
        showApiError(msg);
      }
    } finally {
      setIsQueryResearchLoaded(true);
    }
  };

  const handleAddToTrackedQueries = async () => {
    setIsAddingToTracked(true);

    const adhocQueryValue = adhocQuery?.trim();
    if (!adhocQueryValue) {
      console.error(
        "Cannot add to tracked queries: no associated query found.",
      );
      setIsAddingToTracked(false);
      return;
    }
    const url = `${BACKEND_URL}/api/v1/tracking-page/add-query?query=${encodeURIComponent(adhocQueryValue)}`;

    try {
      const response = await axios.post(
        url,
        {},
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.data.success) {
        setIsAddedToTracked(true);
      } else {
        console.error("Failed to add query:", response.data.message);
        showApiError(
          response.data.message ||
            t(
              "promptResearch.addQueryError",
              "Failed to add query to tracked queries.",
            ),
        );
      }
    } catch (error) {
      console.error("Error adding to tracked queries:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        t(
          "promptResearch.addQueryError",
          "An error occurred while adding the query.",
        );
      showApiError(errorMessage);
    } finally {
      setIsAddingToTracked(false);
    }
  };

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"query-research"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          {/* Heading */}
          <h3 className="h4 fw-bold text-dark">
            {t("promptResearch.title", "Prompt Research")}
          </h3>
          <p className="mt-1 fs-6 text-secondary">
            {t(
              "promptResearch.subtitle",
              "Analyze and optimize your content for specific search prompts",
            )}
          </p>

          {/* SearchBox Component */}
          <QueryResearchSearchBox
            query={query}
            setQuery={setQuery}
            handlePlatformChange={handlePlatformChange}
            handleSearchButton={handleSearchButton}
            isQueryResearchLoaded={isQueryResearchLoaded}
            queryResearchData={queryResearchData}
          />

          {/* Add to Tracked Queries Button */}
          {queryResearchData && !isAddedToTracked && (
            <div className="mb-4">
              <button
                type="button"
                className="btn btn-outline-light"
                onClick={handleAddToTrackedQueries}
                disabled={isAddingToTracked}
                style={{
                  borderColor: "var(--brand-color-dark)",
                  color: "var(--brand-color-dark)",
                }}
              >
                {isAddingToTracked
                  ? t("promptResearch.addingToTracked", "Adding...")
                  : t("promptResearch.addToTracked", "Add to Tracked Prompts")}
              </button>
            </div>
          )}

          {/* Success Message */}
          {isAddedToTracked && (
            <div
              className="alert alert-success d-inline-flex align-items-center mb-4 w-auto"
              role="alert"
            >
              <i className="bi bi-check-circle-fill me-2"></i>
              <div>
                {t(
                  "promptResearch.addedToTracked",
                  "Added For Weekly Processing and Tracked Prompts",
                )}
              </div>
            </div>
          )}

          {/* LLM Provider Filter */}
          {queryResearchData && (
            <div
              className="bg-white p-4 card-radius border shadow-sm mb-4"
              style={{ borderColor: "var(--border)" }}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 fw-bold text-dark">
                  {t("promptResearch.filterByLlm", "Filter by LLM Provider")}
                </h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSelectAllProviders}
                >
                  {selectedProviders.length ===
                  queryResearchData.data.returned_llm_providers.length
                    ? t("common.deselectAll", "Deselect All")
                    : t("common.selectAll", "Select All")}
                </button>
              </div>

              <div className="row g-2">
                {queryResearchData.data.returned_llm_providers.map(
                  (provider) => (
                    <div key={provider} className="col-auto">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`provider-${provider}`}
                          checked={selectedProviders.includes(provider)}
                          onChange={() => handleProviderToggle(provider)}
                          style={{
                            backgroundColor: selectedProviders.includes(
                              provider,
                            )
                              ? "var(--brand-color-dark)"
                              : "#fff",
                            borderColor: selectedProviders.includes(provider)
                              ? "var(--brand-color-dark)"
                              : "#adb5bd",
                          }}
                        />
                        <label
                          className="form-check-label text-capitalize"
                          htmlFor={`provider-${provider}`}
                        >
                          {provider === "chatgpt"
                            ? "ChatGPT"
                            : provider === "gemini"
                              ? "Gemini"
                              : provider === "perplexity"
                                ? "Perplexity"
                                : provider === "claude"
                                  ? "Claude"
                                  : provider.charAt(0).toUpperCase() +
                                    provider.slice(1)}
                        </label>
                      </div>
                    </div>
                  ),
                )}
              </div>

              {selectedProviders.length === 0 && (
                <div className="alert alert-warning mt-3 mb-0" role="alert">
                  <small>
                    {t(
                      "promptResearch.selectAtLeastOneLlm",
                      "Please select at least one LLM provider to view results.",
                    )}
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {queryResearchError && (
            <div className="alert alert-danger" role="alert">
              {t("common.genericError", "An error occurred. Please try again later.")}
            </div>
          )}

          {/* Loading Spinner */}
          {!isQueryResearchLoaded && (
            <div
              className="d-flex flex-column justify-content-center align-items-center text-center py-5"
              style={{ minHeight: "240px" }}
              role="status"
              aria-live="polite"
            >
              <div>
                <HashLoader
                  color="var(--brand-color-dark)"
                  size={60}
                  loading={!isQueryResearchLoaded}
                  aria-label="Loading spinner"
                />
              </div>

              <h4 className="h5 fw-bold text-dark mt-4">
                {t(
                  "promptResearch.hangTight",
                  "Hang tight, we're fetching insights for you...",
                )}
              </h4>

              <div className="mt-2">
                <p className="mb-1 text-muted">
                  {t(
                    "promptResearch.gatheringResults",
                    "Gathering results from",
                  )}{" "}
                  <strong>{LLM_Platforms.join(", ")}</strong>
                </p>
                <p className="mb-0 text-muted">
                  {t(
                    "promptResearch.analyzingBrand",
                    "and analyzing your brand and top competitors.",
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Results Display */}
          {queryResearchData && selectedProviders.length > 0 && (
            <QueryResearchResult
              filteredQueryResearchData={filteredQueryResearchData}
            />
          )}

          {/* No providers selected message */}
          {queryResearchData && selectedProviders.length === 0 && (
            <div className="text-center py-5">
              <h5 className="text-muted">
                {t("promptResearch.noLlmSelected", "No LLM providers selected")}
              </h5>
              <p className="text-muted">
                {t(
                  "promptResearch.selectAtLeastOneLlm",
                  "Please select at least one provider to view the analysis results.",
                )}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default QueryResearch;
